import { useCallback, useMemo } from 'react';

import {
  BackerCause,
  backerKeys,
  useShowCauseBackerInfo,
  useUpdateCauseBackerDirectorySettings,
} from '@givebacks/api/backer';
import { useShowCauseW_Selector } from '@givebacks/api/core';
import { useFormyValidator } from '@givebacks/formy';
import { useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  BadgeProps,
  Button,
  Group,
  ImageMantine,
  Modal,
  Stack,
  Text,
  Title,
  Tooltip,
  notifications,
} from 'ui';
import { UseFormReturnType } from 'ui/Form';

import { getErrorMessage_v2 } from '../../../../../../utils';
import { ClickableOptionsCard } from '../../../../../Cards/ClickableOptionsCard/ClickableOptionsCard';
import {
  ModalTitleWithBackButton,
  useDirectorySettingsModalProvider,
} from '../../DirectorySettingsModalProvider';
import { CauseDirectoryPreview } from './CauseDirectoryPreview';
import {
  CauseDirectorySettingsForm,
  CauseDirectorySettingsFormType,
} from './CauseDirectorySettingsForm';

const DirectoryVisibilityStatusBadge = ({
  visibility,
  causeName,
}: {
  visibility?: BackerCause['visibility'];
  causeName?: string;
}) => {
  const badgeProps = useMemo(() => {
    switch (visibility) {
      case 'private':
        return { type: 'neutral', children: 'Admins Only' };
      case 'public':
        return { type: 'success', children: 'All Contacts' };
      case 'members_only':
        return { type: 'warning', children: 'Members & Admins' };
    }
  }, [visibility]);

  const tooltipContent = useMemo(() => {
    const getAvailability = () => {
      switch (visibility) {
        case 'private':
          return 'only available to admins.';
        case 'members_only':
          return 'only available to current school year members & admins.';
        case 'public':
        default:
          return 'available to all contacts.';
      }
    };
    return `These are the visibility settings for ${causeName ?? 'this cause'}'s directory. This directory is ${getAvailability()}`;
  }, [causeName, visibility]);

  if (!visibility) return null;

  return (
    <Tooltip label={tooltipContent} maw={280}>
      {/* span to pass eventHandlers as Badge doesn't do this properly*/}
      <span>
        <Badge {...(badgeProps as BadgeProps)} variant="filled">
          {badgeProps?.children}
        </Badge>
      </span>
    </Tooltip>
  );
};

const CauseDirectorySettingsModalFooter = () => {
  const { meta, openBack } = useDirectorySettingsModalProvider({
    id: 'settings',
  });
  const { causeId } = meta;
  const { mutate: updateCauseBackerDirectorySettings } = useUpdateCauseBackerDirectorySettings();
  const { useGetForm } = useFormyValidator<{
    directorySettings: UseFormReturnType<CauseDirectorySettingsFormType>;
  }>();
  const form = useGetForm('directorySettings');

  const queryClient = useQueryClient();

  const handleSave = useCallback(() => {
    if (!causeId) return;
    if (!form?.validate().hasErrors) {
      updateCauseBackerDirectorySettings(
        {
          causeId,
          backer: {
            show_in_directory: form?.values?.inDirectory,
            show_email: form?.values?.showEmail,
            show_phone: form?.values?.showPhone,
          },
        },
        {
          onSuccess: () => {
            notifications.show({
              message: 'Directory settings updated',
              variant: 'success',
            });
            // invalidate the cause backer query so that the new values are reflected in the contacts data grid if in view
            void queryClient.invalidateQueries({
              queryKey: backerKeys.causes.id(causeId).backers.all,
            });
            // on success, open back the the base directory settings modal
            openBack();
          },
          onError: (error) => {
            notifications.show({
              message: getErrorMessage_v2(error),
              variant: 'error',
            });
          },
        }
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    causeId,
    form?.values?.inDirectory,
    form?.values?.showEmail,
    form?.values?.showPhone,
    openBack,
    queryClient,
    updateCauseBackerDirectorySettings,
  ]);

  return (
    <Modal.AffixedFooter>
      <Button fullWidth onClick={handleSave}>
        Save Changes
      </Button>
    </Modal.AffixedFooter>
  );
};

const CauseDirectorySettingsModalContent = () => {
  const { meta } = useDirectorySettingsModalProvider({
    id: 'settings',
  });
  const { causeId } = meta;
  const { data: cause } = useShowCauseW_Selector({ uuid: causeId });
  const { data: backerCause } = useShowCauseBackerInfo(causeId);

  if (!causeId) return null;

  return (
    <Stack gap="lg">
      <ClickableOptionsCard>
        <ClickableOptionsCard.Row
          title={cause?.name}
          leftSection={<ImageMantine src={cause?.logo?.url} radius={12} w={48} h={48} />}
          rightSection={<></>}
        />
      </ClickableOptionsCard>
      <Stack>
        <Stack gap={4}>
          <Group gap="xs">
            <Title order={3} fz="lg" flex={1}>
              Directory Visibility
            </Title>
            <DirectoryVisibilityStatusBadge
              visibility={backerCause?.visibility}
              causeName={cause?.name}
            />
          </Group>
          <Text fz="xs" c="gray">
            The settings below allow you to control what information is visible in this Directory.
          </Text>
        </Stack>
        <CauseDirectorySettingsForm causeId={causeId} />
      </Stack>
      <CauseDirectoryPreview causeId={causeId} />
    </Stack>
  );
};

const CauseDirectorySettingsModal = () => {
  const { opened, close } = useDirectorySettingsModalProvider({
    id: 'settings',
  });

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<ModalTitleWithBackButton>Directory Settings</ModalTitleWithBackButton>}
    >
      <CauseDirectorySettingsModalContent />
      <CauseDirectorySettingsModalFooter />
    </Modal>
  );
};

export { CauseDirectorySettingsModal };
